import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { DatastoreCollection } from '@freelancer/datastore';
import {
  TopSkill,
  TopSkillsCollection,
} from '@freelancer/datastore/collections';
import { Feature } from '@freelancer/feature-flags';
import { ModalService } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { CardSize } from '@freelancer/ui/card';
import { trackById, trackByValue } from '@freelancer/ui/helpers';
import { LinkHoverColor } from '@freelancer/ui/link';
import { ListItemPadding, ListItemType } from '@freelancer/ui/list-item';
import { Margin } from '@freelancer/ui/margin';
import { ModalSize } from '@freelancer/ui/modal';
import { FontType } from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-user-profile-skills',
  template: `
    <ng-container *ngIf="skills$ | flAsync as skills; else loading">
      <fl-card
        *ngIf="
          (skills && skills.length > 0) || (isViewerProfileOwner$ | flAsync)
        "
        [size]="CardSize.SMALL"
      >
        <fl-card-header-title i18n="Top skills card title">
          Top Skills
        </fl-card-header-title>

        <fl-card-header-right>
          <fl-button
            *ngIf="showEditSkillsButton$ | flAsync"
            i18n="Top skills edit skills link"
            flTrackingLabel="TopSkillsEditLink"
            [color]="ButtonColor.SECONDARY"
            [size]="ButtonSize.SMALL"
            (click)="showEditSkillsModal()"
          >
            Edit Skills
          </fl-button>
        </fl-card-header-right>

        <ng-container *ngIf="skills && skills.length > 0; else emptyState">
          <fl-list
            [outerPadding]="false"
            [padding]="ListItemPadding.XSMALL"
            [type]="ListItemType.NON_BORDERED"
            [flMarginBottom]="Margin.XSMALL"
          >
            <fl-list-item
              *ngFor="
                let skill of skills | slice: 0:displayAmount;
                trackBy: trackById
              "
            >
              <app-user-profile-skills-item
                [skill]="skill"
              ></app-user-profile-skills-item>
            </fl-list-item>
          </fl-list>
          <fl-link
            *ngIf="displayAmount < skills.length"
            class="ViewMoreLink"
            i18n="Top skills view more link"
            flTrackingLabel="TopSkillsViewMoreLink"
            (click)="showMore()"
          >
            View More
          </fl-link>
        </ng-container>

        <ng-template #emptyState>
          <app-user-profile-skills-empty
            (addSkills)="showEditSkillsModal()"
          ></app-user-profile-skills-empty>
        </ng-template>
      </fl-card>
    </ng-container>

    <ng-template #loading>
      <fl-card [size]="CardSize.SMALL">
        <fl-card-header-title i18n="Top skills card title">
          Top Skills
        </fl-card-header-title>
        <fl-list
          [outerPadding]="false"
          [padding]="ListItemPadding.XSMALL"
          [type]="ListItemType.NON_BORDERED"
        >
          <fl-list-item *ngFor="let item of [0, 1, 2]; trackBy: trackByValue">
            <fl-loading-text [rows]="1" [padded]="false"></fl-loading-text>
          </fl-list-item>
        </fl-list>
      </fl-card>
    </ng-template>
  `,
  styleUrls: ['./user-profile-skills.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileSkillsComponent implements OnInit {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  CardSize = CardSize;
  FontType = FontType;
  LinkHoverColor = LinkHoverColor;
  ListItemPadding = ListItemPadding;
  ListItemType = ListItemType;
  Margin = Margin;
  Feature = Feature;
  trackById = trackById;
  trackByValue = trackByValue;

  @Input() isLoggedIn$: Rx.Observable<boolean>;
  @Input() isViewerProfileOwner$: Rx.Observable<boolean>;
  @Input() skillsCollection: DatastoreCollection<TopSkillsCollection>;
  @Input() userId$: Rx.Observable<number>;

  @Output() triggerSkillsUpdate = new EventEmitter<undefined>();

  readonly INITIAL_DISPLAY_AMOUNT = 5;
  displayAmount = this.INITIAL_DISPLAY_AMOUNT;

  showEditSkillsButton$: Rx.Observable<boolean>;
  skills$: Rx.Observable<readonly TopSkill[]>;

  constructor(private modalService: ModalService) {}

  ngOnInit(): void {
    this.skills$ = this.skillsCollection.valueChanges();

    this.showEditSkillsButton$ = Rx.combineLatest([
      this.isViewerProfileOwner$,
      this.skills$,
    ]).pipe(
      map(
        ([isViewerProfileOwner, skills]) =>
          isViewerProfileOwner && skills.length > 0,
      ),
    );
  }

  showMore(): void {
    this.displayAmount += 4;
  }

  showEditSkillsModal(): void {
    this.modalService
      .open('SkillsEditModal', {
        size: ModalSize.XLARGE,
        edgeToEdge: true,
        mobileFullscreen: true,
      })
      .afterClosed()
      .toPromise()
      .then(result => {
        // If the modal returns true, changes were made to the users skills so
        // force the datastore to reload the users top skills.
        if (result) {
          this.triggerSkillsUpdate.emit();
        }
      });
  }
}
